import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag, ResolverTag } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-shared-components';
import { Translation } from '@ps/web-shared-components';
import { isArray } from 'lodash';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { styles as sharedStyles } from '@swf/account';
import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import MinIcon from '@swf/core/assets/icons/minus-icon.svg';
import ExpandIcon from '@swf/core/assets/icons/plus-icon.svg';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';

import FormContentDESH from '../../shared/TakeABreakFormContentDESH/FormContentDESH';
import { ExclusionTime } from '../../shared/definitions';
// TODO refactor to use composition or shared.scss
// eslint-disable-next-line no-restricted-imports
import styles from '../TakeABreakFromUs.scss';
import useExclusionInfoMessages from '../hooks/useExclusionInfoMessages';

import InfoBlock from './InfoBlock/InfoBlock';
import InfoLink from './InfoLink/InfoLink';
import LearnMoreAboutRG from './LearnMoreAboutRG/LearnMoreAboutRG';
import {
  TXTCLI_Self_Exclusion_during_Tourname_ELL,
  TXTCLI_YesX,
  TXTWCL_Alternatively_you_can_choose_t_ELL,
  TXTWCL_Can_I_take_a_break_from_all_ga_ELL,
  TXTWCL_Can_you_exclude_me_permanently_ELL,
  TXTWCL_DonXt_worryX_weXll_automatical_ELL,
  TXTWCL_For_future_tournamentsX_weXll__ELL,
  TXTWCL_How_long_can_I_be_excluded_for_ELL,
  TXTWCL_How_long_can_I_take_a_break_fo_ELL,
  TXTWCL_If_you_have_chosen_to_indefini_ELL,
  TXTWCL_If_you_permanently_self_exclud_ELL,
  TXTWCL_If_you_take_a_break_between_N0_ELL,
  TXTWCL_In_case_you_are_interestedX_we_ELL,
  TXTWCL_ItXs_basically_a_pause_on_your_ELL,
  TXTWCL_ItXs_basically_a_pause_on_your_ELL_1,
  TXTWCL_Just_contact_us_through_the_Xa_ELL,
  TXTWCL_Known_as_XbXself_exclusionX_bX_ELL,
  TXTWCL_Known_as_self_exclusionX_it_es_ELL,
  TXTWCL_Of_courseX_1,
  TXTWCL_Of_courseX_Just_XaXcontact_usX_ELL,
  TXTWCL_See_our_XaXSelf_Exclusion_duri_ELL,
  TXTWCL_Simply_visit_XaXwwwXgamstopXco_ELL,
  TXTWCL_This_can_be_anywhere_from_1_da_ELL,
  TXTWCL_This_can_be_anywhere_from_N0_d_ELL,
  TXTWCL_This_can_be_anywhere_from_N0_d_ELL_1,
  TXTWCL_This_can_be_anywhere_from_N0_h_ELL,
  TXTWCL_This_can_be_anywhere_from_N0_h_ELL_1,
  TXTWCL_This_can_be_anywhere_from_N0_h_ELL_2,
  TXTWCL_WeXll_automatically_remove_you_ELL_1,
  TXTWCL_What_does_this_meanX,
  TXTWCL_What_if_IXve_signed_up_to_tour_ELL,
  TXTWCL_Where_can_I_ask_for_advice_on__ELL,
  TXTWCL_XbXWhat_happens_when_my_break__ELL,
  TXTWCL_You_can_choose_how_long_to_tak_ELL,
  TXTWCL_You_can_choose_how_long_to_tak_ELL_1,
  TXTWCL_You_can_choose_how_long_to_tak_ELL_2,
  TXTWCL_You_can_choose_how_long_to_tak_ELL_3,
  TXTWCL_You_can_choose_how_long_to_tak_ELL_4,
  TXTWCL_You_can_complete_any_tournamen_ELL,
  TXTWCL_You_can_do_this_by_XaXcontacti_ELL,
  TXTWCL_You_can_still_complete_any_tou_ELL,
  TXTWCL_You_choose_the_start_and_end_d_ELL_1
} from './TakeABreakFromUsInformation.messages.json';

interface QuestionAnswerTagsPair {
  infoQuestion: TranslationTag;
  infoAnswer: TranslationTag | TranslationTag[];
}

const daysPerYear = 365;
const exclusionPeriod = {
  [Sites.USMI]: [ExclusionTime.ThreeDays, ExclusionTime.FiveYears / daysPerYear],
  [Sites.US_PA]: [ExclusionTime.ThreeDays, ExclusionTime.OneYear / daysPerYear],
  [Sites.UK]: [Math.abs(ExclusionTime.HalfDay), ExclusionTime.FiveYears / daysPerYear],
  [Sites.SWE]: [Math.abs(ExclusionTime.TwentyFourHours), ExclusionTime.OneYear / daysPerYear],
  [Sites.DE_SH]: [Math.abs(ExclusionTime.TwentyFourHours), ExclusionTime.HundredEightyDays],
  [Sites.CA_ON]: [Math.abs(ExclusionTime.TwentyFourHours), ExclusionTime.NinetyDays],
  default: [Math.abs(ExclusionTime.HalfDay), ExclusionTime.HundredEightyDays]
};

const exclusionPeriodText = {
  [Sites.USMI]: TXTWCL_This_can_be_anywhere_from_N0_d_ELL,
  [Sites.US_PA]: TXTWCL_This_can_be_anywhere_from_N0_d_ELL_1,
  [Sites.SWE]: TXTWCL_This_can_be_anywhere_from_N0_h_ELL_1,
  [Sites.UK]: TXTWCL_This_can_be_anywhere_from_N0_h_ELL_2,
  default: TXTWCL_This_can_be_anywhere_from_N0_h_ELL
};

const TakeABreakFromUsInformation: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const site = useSelector(select.cms.getSiteId);
  const showVegasContent = useSelector(select.cms.showVegasContent);
  const isEUDE = useSelector(select.account.isEUDE);
  const isIndefiniteExclusionAllowed = useSelector(select.cms.isIndefiniteExclusionAllowed);
  const exclusionInfoMessages = useExclusionInfoMessages();

  const visibilityOASISExclusionOption = useFeature(FeatureFlag.VisibilityOASISExclusionOption);

  const getInitialParagraphSentence = () => {
    switch (true) {
      case visibilityOASISExclusionOption:
        return TXTWCL_Known_as_XbXself_exclusionX_bX_ELL;
      case site === Sites.CA_ON:
        return TXTWCL_ItXs_basically_a_pause_on_your_ELL_1;
      case [Sites.DEN, Sites.FRA, Sites.GR, Sites.US_PA, Sites.US_NJ, Sites.POR].includes(site):
        return TXTWCL_Known_as_self_exclusionX_it_es_ELL;
      default:
        return TXTWCL_ItXs_basically_a_pause_on_your_ELL;
    }
  };

  const renderExclusionPeriodText = () => {
    switch (true) {
      case site === Sites.DEN:
        return <Translation {...TXTWCL_You_can_choose_how_long_to_tak_ELL_4} values={{ 0: 24 }} />;
      case site === Sites.FRA:
        return <Translation tagName="span" {...TXTWCL_You_can_choose_how_long_to_tak_ELL} />;
      case site === Sites.GR:
        return <Translation tagName="span" {...TXTWCL_You_can_choose_how_long_to_tak_ELL_1} />;
      case site === Sites.US_PA:
        return (
          <Translation
            tagName="span"
            {...TXTWCL_You_can_choose_how_long_to_tak_ELL_2}
            values={{ 0: ExclusionTime.ThreeDays }}
          />
        );
      case site === Sites.POR:
        return <Translation {...TXTWCL_You_can_choose_how_long_to_tak_ELL_4} values={{ 0: 12 }} />;
      case site === Sites.US_NJ:
        return (
          <Translation
            tagName="span"
            {...TXTWCL_You_can_choose_how_long_to_tak_ELL_3}
            values={{ 0: ExclusionTime.ThreeDays, 1: 5 }}
          />
        );
      case site === Sites.UK:
        return (
          <>
            <Translation tagName="span" {...TXTWCL_You_choose_the_start_and_end_d_ELL_1} />
            <Translation {...TXTWCL_This_can_be_anywhere_from_1_da_ELL} />
          </>
        );
      case isEUDE:
        return (
          <>
            <Translation tagName="span" {...TXTWCL_You_choose_the_start_and_end_d_ELL_1} />
            <Translation
              {...TXTWCL_This_can_be_anywhere_from_N0_d_ELL_1}
              values={{ 0: ExclusionTime.NinetyDays }}
            />
          </>
        );
      default:
        return (
          <>
            <Translation tagName="span" {...TXTWCL_You_choose_the_start_and_end_d_ELL_1} />
            <Translation
              {...(exclusionPeriodText[site] ?? exclusionPeriodText.default)}
              values={{
                0: (exclusionPeriod[site] || exclusionPeriod.default)[0],
                1: (exclusionPeriod[site] || exclusionPeriod.default)[1]
              }}
            />
          </>
        );
    }
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.infoTitle} onClick={() => setIsExpanded(!isExpanded)}>
        <div className={sharedStyles.formHeader}>
          <Translation {...TXTWCL_What_does_this_meanX} />
        </div>
        <div className={styles.expandIcon}>{!isExpanded ? <ExpandIcon /> : <MinIcon />}</div>
      </div>
      {isExpanded && (
        <>
          <p>
            <Translation {...getInitialParagraphSentence()} />
          </p>
          {!visibilityOASISExclusionOption && (
            <InfoBlock
              title={
                site === Sites.EST
                  ? TXTWCL_How_long_can_I_be_excluded_for_ELL
                  : TXTWCL_How_long_can_I_take_a_break_fo_ELL
              }
            >
              <div>
                {renderExclusionPeriodText()}{' '}
                {isIndefiniteExclusionAllowed && (
                  <Translation {...TXTWCL_Alternatively_you_can_choose_t_ELL} />
                )}
              </div>
            </InfoBlock>
          )}
          {site === Sites.US_PA && (
            <InfoBlock title={TXTWCL_XbXWhat_happens_when_my_break__ELL}>
              <div>
                <Translation
                  {...TXTWCL_If_you_take_a_break_between_N0_ELL}
                  values={{ 0: ExclusionTime.ThreeDays, 1: ExclusionTime.HundredEightyDays }}
                />{' '}
                <Translation
                  {...TXTWCL_If_you_have_chosen_to_indefini_ELL}
                  values={{ 0: ExclusionTime.TwelveMonths }}
                />{' '}
                <Translation
                  {...TXTWCL_You_can_do_this_by_XaXcontacti_ELL}
                  values={{
                    a: (msg: string) => (
                      <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
                    )
                  }}
                />
              </div>
            </InfoBlock>
          )}
          {exclusionInfoMessages.map(
            ({ infoQuestion, infoAnswer }: QuestionAnswerTagsPair, index: number) => (
              <InfoBlock title={infoQuestion} key={index}>
                {isArray(infoAnswer) ? (
                  <div>
                    {infoAnswer.map((answerSentence, sentenceIndex) => (
                      <React.Fragment key={sentenceIndex}>
                        <Translation
                          tagName="span"
                          {...answerSentence}
                          values={{ 0: ExclusionTime.HundredEightyDays }}
                        />{' '}
                      </React.Fragment>
                    ))}
                  </div>
                ) : (
                  <Translation tagName="div" {...infoAnswer} />
                )}
              </InfoBlock>
            )
          )}
          {[Sites.FRA, Sites.GR, Sites.US_PA, Sites.US_NJ].includes(site) && (
            <InfoBlock title={TXTWCL_What_if_IXve_signed_up_to_tour_ELL}>
              <div>
                <Translation
                  {...(site === Sites.US_PA
                    ? TXTWCL_You_can_still_complete_any_tou_ELL
                    : TXTWCL_DonXt_worryX_weXll_automatical_ELL)}
                />{' '}
                {site === Sites.US_PA && (
                  <Translation {...TXTWCL_For_future_tournamentsX_weXll__ELL} />
                )}{' '}
                <ResolverLink resolverTag={ResolverTag.HelpCenter}>
                  <Translation {...TXTCLI_Self_Exclusion_during_Tourname_ELL} />
                </ResolverLink>
              </div>
            </InfoBlock>
          )}
          {site === Sites.DEN && (
            <InfoBlock title={TXTWCL_What_if_IXve_signed_up_to_tour_ELL}>
              <div>
                <Translation {...TXTWCL_You_can_complete_any_tournamen_ELL} />{' '}
                <Translation {...TXTWCL_WeXll_automatically_remove_you_ELL_1} />{' '}
                <Translation {...TXTWCL_If_you_permanently_self_exclud_ELL} />{' '}
                <Translation
                  {...TXTWCL_See_our_XaXSelf_Exclusion_duri_ELL}
                  values={{
                    a: (msg: string) => (
                      <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
                    )
                  }}
                />
              </div>
            </InfoBlock>
          )}
          {!showVegasContent &&
            ![
              Sites.UK,
              Sites.FRA,
              Sites.DEN,
              Sites.GR,
              Sites.US_PA,
              Sites.US_NJ,
              Sites.POR
            ].includes(site) &&
            !isIndefiniteExclusionAllowed && (
              <InfoBlock title={TXTWCL_Can_you_exclude_me_permanently_ELL}>
                <div>
                  {site === Sites.EST ? (
                    <InfoLink
                      textTranslation={TXTWCL_Of_courseX_1}
                      linkTranslation={TXTWCL_Just_contact_us_through_the_Xa_ELL}
                      resolverTag={ResolverTag.HelpCenter}
                    />
                  ) : (
                    <InfoLink
                      linkTranslation={TXTWCL_Of_courseX_Just_XaXcontact_usX_ELL}
                      resolverTag={
                        site === Sites.CA_ON ? ResolverTag.RGQuestion : ResolverTag.ContactSupport
                      }
                    />
                  )}
                </div>
              </InfoBlock>
            )}
          {site === Sites.UK && (
            <InfoBlock title={TXTWCL_Can_I_take_a_break_from_all_ga_ELL}>
              <div>
                <InfoLink
                  textTranslation={TXTCLI_YesX}
                  linkTranslation={TXTWCL_Simply_visit_XaXwwwXgamstopXco_ELL}
                  resolverTag={ResolverTag.SelfExclusionGamStop}
                />
              </div>
            </InfoBlock>
          )}
          {site === Sites.DE_SH && (
            <InfoBlock title={TXTWCL_Where_can_I_ask_for_advice_on__ELL}>
              <FormContentDESH informationTag={TXTWCL_In_case_you_are_interestedX_we_ELL} />
            </InfoBlock>
          )}
          {site === Sites.POR && (
            <InfoBlock title={TXTWCL_What_if_IXve_signed_up_to_tour_ELL}>
              <div>
                <Translation {...TXTWCL_DonXt_worryX_weXll_automatical_ELL} />{' '}
                <Translation
                  {...TXTWCL_See_our_XaXSelf_Exclusion_duri_ELL}
                  values={{
                    a: (msg: string) => (
                      <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
                    )
                  }}
                />
              </div>
            </InfoBlock>
          )}
          <LearnMoreAboutRG />
        </>
      )}
    </div>
  );
};

export default TakeABreakFromUsInformation;
